// MATERIAL-UI
import { Modal, CircularProgress, IconButton } from "@mui/material";

//REDUX
import { useAppDispatch } from "src/store/store";
import {
  useResendWithdrawalEmailCodeMutation,
  useCheckIsUserLockedQuery,
  HoldingRes,
  useProcessFiatWithdrawalMutation,
} from "src/store/features/api/generated";

// STYLES
import {
  StyledMainContainer,
  StyledModalTitle,
  StyledNextButton,
  StyledTitleContainer,
  StyledWithdrawalBox,
} from "../common/styles/FiatWithdrawalStyled";

// COMPONENTS
import { FiatWithdrawalStageInputDetails } from "./FiatWithdrawalStageInputDetails";
import { FiatWithdrawalStageReview } from "./FiatWithdrawalStageReview";
import { FiatWithdrawalStageVerification } from "./FiatWithdrawalStageVerification";
import { FiatWithdrawalStageCompleted } from "./FiatWithdrawalStageCompleted";
import {
  ShowInsufficientBalanceComponent,
  ShowMinAmountErrorComponent,
  ShowActionRequiredComponent,
  ShowRecentWithdrawalErrorComponent,
} from "./WithdrawalErrorComponents";

// ICONS
import BackIcon from "src/icons/BackIcon";
import { Close } from "@mui/icons-material";

// REDUX
import {
  setFiatWithdrawalStage,
  resetWithdrawalThunk,
  inputStageThunk,
  setError,
  useWithdrawalSlice,
  setIsUnderMaintenance,
  setWithdrawalAmount,
  setIsFiatWithdrawalModalOpen,
} from "src/store/features/withdrawal.slice";

// AMPLITUTDE
import { trackEvent } from "src/Amplitude";

// UTILS
import { getSessionItem } from "src/utils/SessionManagement";

// HOOKS
import { useEffect, useState, useCallback, useRef } from "react";
import { useUserSlice } from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";
import useTransmitRisk from "src/utils/hooks/useTransmitRisk";
import useTransactionDisclosure, {
  type DisclosureMetadata,
} from "src/utils/hooks/useTransactionDisclosure";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";

export interface BankAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  currency: string;
  isDefault: boolean;
}

interface FiatWithdrawalModalProps {
  bankAccounts: BankAccount[];
  defaultCurrencyHolding?: HoldingRes;
}

export function FiatWithdrawalModal({
  bankAccounts,
  defaultCurrencyHolding,
}: FiatWithdrawalModalProps) {
  const dispatch = useAppDispatch();
  const currentEmail = getSessionItem("email");

  const { isGuest } = usePermission();

  const { is2FAEnabled, userHoldings } = useUserSlice();

  const { executeTransmitRisk } = useTransmitRisk();

  const {
    selectedBankAccount,
    fiatWithdrawalStage,
    withdrawalAmount,
    isFiatWithdrawalModalOpen,
    code2fa,
    emailCode,
    isUnderMaintenance,
  } = useWithdrawalSlice();

  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  // Double-submit guard: ref blocks re-entry synchronously, state disables the
  // button for the full submit span (isProcessing misses the transmit-risk call).
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    transactionDisclosure,
    requiresDisclosure,
    isLoading: isDisclosureLoading,
    disclosureMetadata: acceptedDisclosureMetadata,
    disclosurePayload,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure((disclosureId: string, metadata?: DisclosureMetadata) => {
    // The modal only opens from the Withdraw press, so accepting always continues.
    processFiatWithdrawalRequest(metadata);
    setShowDisclosureModal(false);
  }, "fiat_withdrawal", "withdrawal-disclosure");

  const disclosureModalProps = {
    ...modalProps,
    open: showDisclosureModal && !!transactionDisclosure,
    onClose: () => {
      setShowDisclosureModal(false);
      handleDisclosureClose();
    },
  };

  const [sendEmail, { isLoading: emailSending }] = useResendWithdrawalEmailCodeMutation();
  const [processFiatWithdrawal, { isLoading: isProcessing }] = useProcessFiatWithdrawalMutation();
  const {
    data: isLocked,
    refetch,
    error: errorIsLocked,
  } = useCheckIsUserLockedQuery({ fiat: true }, { skip: isGuest });

  const [inputErrors, setInputErrors] = useState({ selectError: false, amountError: false });
  const [showMinAmountError, setShowMinAmountError] = useState(false);
  const [showActionRequired, setShowActionRequired] = useState(false);
  const [showRecentWithdrawalError, setShowRecentWithdrawalError] = useState(false);
  const [recentWithdrawalTimestamp, setRecentWithdrawalTimestamp] = useState<string>("");
  const [showNoCurrencyError, setShowNoCurrencyError] = useState(false);
  const [selectedAccountHolding, setSelectedAccountHolding] = useState<HoldingRes | undefined>(
    defaultCurrencyHolding,
  );

  // Extract fiat currencies from user holdings
  const userFiatHoldings =
    userHoldings?.filter((holding) => holding.currencyConfig?.type === "Fiat") || [];

  // Check if selected bank account currency matches any user fiat holdings
  const hasMatchingFiatCurrency =
    selectedBankAccount &&
    userFiatHoldings.some(
      (holding) => holding.currencyConfig?.code === selectedBankAccount.currency,
    );

  // Set error when user doesn't have the required currency
  useEffect(() => {
    if (selectedBankAccount && !hasMatchingFiatCurrency) {
      setShowNoCurrencyError(true);
    } else {
      setShowNoCurrencyError(false);
    }
  }, [selectedBankAccount, hasMatchingFiatCurrency]);

  useEffect(() => {
    if (selectedBankAccount) {
      const holding = userFiatHoldings.find(
        (holding) => holding.currencyConfig?.code === selectedBankAccount.currency,
      );
      setSelectedAccountHolding(holding);
    } else {
      setSelectedAccountHolding(defaultCurrencyHolding);
    }
  }, [selectedBankAccount, userFiatHoldings, defaultCurrencyHolding]);

  const handleInputClick = async () => {
    refetch();

    if (!is2FAEnabled) {
      setShowActionRequired(true);
      return;
    }
    if (isLocked) {
      dispatch(
        setError(
          "You have recently submitted too many failed withdrawal attempts. Please try again in a few minutes.",
        ),
      );
      return;
    }

    dispatch(setError(""));
    setShowMinAmountError(false);
    trackEvent(
      "Fiat Withdrawal Next Button",
      { stage: fiatWithdrawalStage },
      "Fiat Withdrawal Modal",
    );

    if (!selectedBankAccount) {
      dispatch(setError("Please select a bank account"));
      return;
    }

    if (!withdrawalAmount || withdrawalAmount <= 0) {
      dispatch(setError("Invalid amount"));
      return;
    }

    if (!withdrawalAmount || withdrawalAmount < 100) {
      setShowMinAmountError(true);
      return;
    }

    if (selectedAccountHolding && withdrawalAmount > selectedAccountHolding.available) {
      // Do not set error, just show custom box
      return;
    }

    try {
      dispatch(setFiatWithdrawalStage("Review"));
      dispatch(setError(""));
    } catch (error: any) {
      // dispatch(setError(error.message));
    }
  };

  const handleReviewClick = async () => {
    dispatch(setError(""));
    trackEvent(
      "Fiat Withdrawal Next Button",
      { stage: fiatWithdrawalStage },
      "Fiat Withdrawal Modal",
    );

    if (selectedBankAccount) {
      await sendEmail({
        sendWithdrawalEmailCodeDto: {
          cryptoAddress: selectedBankAccount.accountNumber,
          token: selectedBankAccount.currency,
          amount: withdrawalAmount.toString(),
          type: "Fiat",
        },
      })
        .unwrap()
        .then(() => {
          dispatch(setFiatWithdrawalStage("Verification"));
        })
        .catch((error) => {
          dispatch(setError("Request for email verification failed"));
        });
    }
  };

  const processFiatWithdrawalRequest = async (disclosureMetadata?: DisclosureMetadata) => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      dispatch(setError(""));
      trackEvent(
        "Processing Fiat Withdrawal Request",
        { email: currentEmail },
        "Fiat Withdrawal Modal",
      );

      // Capture a fresh transaction risk snapshot immediately before submitting.
      // The result is stored in riskRecommendations on the backend; the withdrawal
      // service reads from there instead of loginHistory.
      await executeTransmitRisk("transaction", undefined, {
        amount: withdrawalAmount || 0,
        currency: selectedBankAccount?.currency,
        reason: "fiat-withdrawal",
      }).catch(() => {
        // Non-blocking — backend treats a missing risk record as high-risk automatically.
      });

      const timezone =
        typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
      const disclosureFields = (disclosurePayload || disclosureMetadata) && {
        disclosureRequestDateTime:
          disclosurePayload?.disclosureRequestDateTime ||
          disclosureMetadata?.disclosureRequestDateTime,
        disclosureAcceptanceDateTime:
          disclosurePayload?.disclosureAcceptanceDateTime ||
          disclosureMetadata?.disclosureAcceptanceDateTime,
        timezone: timezone,
      };
      await processFiatWithdrawal({
        processFiatWithdrawalDto: {
          bankAccountId: selectedBankAccount?.id || "",
          currency: selectedBankAccount?.currency || "",
          amount: withdrawalAmount || 0,
          emailCode: emailCode || "",
          code: code2fa || "",
          ...(disclosureFields ?? {}),
        },
      })
        .unwrap()
        .then(() => {
          dispatch(setFiatWithdrawalStage("Completed"));
        })
        .catch((error) => {
          // RTK Query errors carry the server message on error.data.message.
          dispatch(
            setError(
              error?.data?.message ||
                error?.message ||
                "Failed to process withdrawal. Please try again.",
            ),
          );
        });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleVerificationClick = async () => {
    // Still resolving — requiresDisclosure === false means "unknown", not "not required"
    if (isDisclosureLoading) {
      return;
    }

    // Disclosure gate — once accepted today, fall through and submit instead of re-prompting
    if (requiresDisclosure && !acceptedDisclosureMetadata) {
      if (!showDisclosureModal) {
        setShowDisclosureModal(true);
      }
      return;
    }

    await processFiatWithdrawalRequest(undefined);
  };

  const handleClose = () => {
    dispatch(resetWithdrawalThunk());
    dispatch(setIsFiatWithdrawalModalOpen(false));
  };

  const handleBackButton = () => {
    // clear input values so its not cached
    dispatch(inputStageThunk());
  };

  const handleModalProcess = (fiatWithdrawalStage: string) => {
    if (fiatWithdrawalStage === "InputDetails") handleInputClick();
    else if (fiatWithdrawalStage === "Review") handleReviewClick();
    else if (fiatWithdrawalStage === "Verification") handleVerificationClick();
  };

  const handleMaxAmount = () => {
    if (selectedAccountHolding?.available) {
      dispatch(setWithdrawalAmount(selectedAccountHolding.available));
    }
  };

  useEffect(() => {
    if (
      errorIsLocked &&
      errorIsLocked.message?.includes("Withdrawals are currently under maintenance on Portal")
    ) {
      dispatch(setIsUnderMaintenance(true));
      dispatch(setError(errorIsLocked.message));
    } else if (errorIsLocked && errorIsLocked.message?.includes("24 hours")) {
      // Extract timestamp from error message
      const message = errorIsLocked.message;
      const timestampMatch = message.match(/expires at (.+)$/);
      if (timestampMatch) {
        const timestamp = timestampMatch[1];
        setRecentWithdrawalTimestamp(timestamp);
        setShowRecentWithdrawalError(true);
        dispatch(setError("")); // Clear the error to avoid showing both
      }
    } else {
      // avoid caching incorrect timestamp
      setShowRecentWithdrawalError(false);
      setRecentWithdrawalTimestamp("");
    }
  }, [errorIsLocked]);

  // Reset showMinAmountError if amount is sufficient
  useEffect(() => {
    if (withdrawalAmount >= 100) {
      setShowMinAmountError(false);
    }
  }, [withdrawalAmount]);

  const buttonText = emailSending
    ? "Sending verification email"
    : fiatWithdrawalStage === "Verification"
    ? "Withdraw"
    : "Next";

  const isLoading = isProcessing || isSubmitting || emailSending;

  const showInsufficientBalance =
    fiatWithdrawalStage === "InputDetails" &&
    selectedAccountHolding &&
    selectedBankAccount &&
    hasMatchingFiatCurrency &&
    withdrawalAmount > selectedAccountHolding.available;

  // Add Enter key handler
  const isNextDisabled =
    isLoading ||
    showActionRequired ||
    showRecentWithdrawalError ||
    showInsufficientBalance ||
    showNoCurrencyError ||
    bankAccounts.length === 0 ||
    inputErrors.selectError ||
    inputErrors.amountError ||
    !selectedBankAccount ||
    !withdrawalAmount ||
    showMinAmountError ||
    isGuest ||
    !is2FAEnabled;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        !isNextDisabled &&
        isFiatWithdrawalModalOpen &&
        fiatWithdrawalStage !== "Completed" &&
        !isUnderMaintenance
      ) {
        e.preventDefault();
        handleModalProcess(fiatWithdrawalStage);
      }
    },
    [isNextDisabled, isFiatWithdrawalModalOpen, fiatWithdrawalStage, isUnderMaintenance],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Determine modal title based on stage
  let modalTitle = "Withdraw Fiat";
  if (fiatWithdrawalStage === "Completed") {
    modalTitle = "Request Completed";
  } else if (fiatWithdrawalStage === "Review") {
    modalTitle = "Review Details";
  } else if (fiatWithdrawalStage === "Verification") {
    modalTitle = "Email & 2FA Security";
  }

  return (
    <>
      <Modal open={isFiatWithdrawalModalOpen} onClose={handleClose}>
        <StyledWithdrawalBox>
          <StyledTitleContainer>
            {fiatWithdrawalStage !== "InputDetails" && fiatWithdrawalStage !== "Completed" && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2, cursor: "pointer" }}
                onClick={handleBackButton}>
                <BackIcon width={18} height={18} />
              </IconButton>
            )}
            <StyledModalTitle sx={{ flexGrow: 1, textAlign: "center" }}>
              {modalTitle}
            </StyledModalTitle>
            <Close onClick={handleClose} style={{ cursor: "pointer" }} />
          </StyledTitleContainer>
          <StyledMainContainer id="fiat-withdrawal-modal">
            {/* Show Action Required if 2FA is not enabled */}
            {showActionRequired && <ShowActionRequiredComponent />}

            {/* Show Recent Withdrawal Error if user has made a withdrawal within 24 hours */}
            {showRecentWithdrawalError && (
              <ShowRecentWithdrawalErrorComponent dateTime={recentWithdrawalTimestamp} />
            )}

            {/* Stage 1: Entering withdrawal request details */}
            {fiatWithdrawalStage === "InputDetails" && !isUnderMaintenance && (
              <FiatWithdrawalStageInputDetails
                selectedBankAccount={selectedBankAccount}
                withdrawalAmount={withdrawalAmount}
                bankAccounts={bankAccounts}
                defaultCurrencyHolding={selectedAccountHolding}
                onMaxAmount={handleMaxAmount}
                onInputErrorChange={setInputErrors}
                showMinAmountError={showMinAmountError || false}
                showInsufficientBalance={showInsufficientBalance || false}
                showNoCurrencyError={showNoCurrencyError || false}
              />
            )}

            {/* Custom insufficient balance error box */}
            {showInsufficientBalance && selectedAccountHolding && (
              <ShowInsufficientBalanceComponent
                available={selectedAccountHolding.available}
                currency={selectedBankAccount?.currency}
              />
            )}

            {/* Custom minimum amount error box */}
            {showMinAmountError && (
              <ShowMinAmountErrorComponent currency={selectedBankAccount?.currency} />
            )}

            {/* Stage 2: Review */}
            {fiatWithdrawalStage === "Review" && !isUnderMaintenance && (
              <FiatWithdrawalStageReview
                selectedBankAccount={selectedBankAccount}
                withdrawalAmount={withdrawalAmount}
                defaultCurrencyHolding={selectedAccountHolding}
              />
            )}

            {/* Stage 3: Authenticating the user */}
            {fiatWithdrawalStage === "Verification" && !isUnderMaintenance && (
              <FiatWithdrawalStageVerification
                selectedBankAccount={selectedBankAccount}
                withdrawalAmount={withdrawalAmount}
                isLoading={isProcessing || isSubmitting}
                defaultCurrencyHolding={selectedAccountHolding}
              />
            )}

            {/* Stage 4: Completion*/}
            {fiatWithdrawalStage === "Completed" && !isUnderMaintenance && (
              <FiatWithdrawalStageCompleted
                selectedBankAccount={selectedBankAccount}
                withdrawalAmount={withdrawalAmount}
                bankAccounts={bankAccounts}
                defaultCurrencyHolding={selectedAccountHolding}
              />
            )}
            {fiatWithdrawalStage !== "Completed" && !isUnderMaintenance && (
              <StyledNextButton
                onClick={() => handleModalProcess(fiatWithdrawalStage)}
                fullWidth
                disabled={isNextDisabled}>
                {buttonText}
                {(isProcessing || isSubmitting) && (
                  <CircularProgress size={20} style={{ marginLeft: "10px" }} />
                )}
              </StyledNextButton>
            )}
          </StyledMainContainer>
        </StyledWithdrawalBox>
      </Modal>
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </>
  );
}
